import { useCallback, useEffect, useState } from "react";
import { Activity, Loader2, AlertTriangle, Fingerprint } from "lucide-react";
import { api } from "@/lib/api";

const STATE_STYLE = {
  "Receiving Scans": { dot: "#0E8A46", pill: "bg-[#E9F9EF] text-[#0E8A46]" },
  "Webhook Healthy": { dot: "#0E8A46", pill: "bg-[#E9F9EF] text-[#0E8A46]" },
  "No Recent Activity": { dot: "#B5590F", pill: "bg-[#FFF1E6] text-[#B5590F]" },
  "Processing Error": { dot: "#C8102E", pill: "bg-[#FDECEC] text-[#C0362C]" },
};

const useHealth = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/attendance/fingerprint/health").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);
  return data;
};

const Metric = ({ label, value, sub, title, testid }) => (
  <div className="rounded-xl bg-black/[0.03] px-3 py-2.5" title={title}>
    <p className="text-[10.5px] uppercase tracking-wide text-[#8E8E93]">{label}</p>
    <p data-testid={testid} className="num mt-0.5 text-[14px] font-semibold text-[#1D1D1F]">{value}</p>
    {sub && <p className="num mt-0.5 text-[11px] text-[#8E8E93]">{sub}</p>}
  </div>
);

export function DeviceHealthCompact() {
  const data = useHealth();
  const st = STATE_STYLE[data?.webhook_state] || STATE_STYLE["No Recent Activity"];
  return (
    <div data-testid="device-health-compact" className="liquid-glass-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1D1D1F]">
          <Fingerprint className="h-4 w-4 text-[#0071E3]" /> Fingerspot
        </div>
        {data ? (
          <span data-testid="device-health-state" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium ${st.pill}`}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: st.dot }} /> {data.webhook_state}
          </span>
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-[#B0B0B8]" />
        )}
      </div>
      {data && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Metric testid="dh-last-scan" label="Last Scan" value={data.last_scan_rel} sub={data.last_scan_wita ? `${data.last_scan_wita} WITA` : null} title={data.last_scan_wita ? `${data.last_scan_wita} WITA` : ""} />
          <Metric testid="dh-scans-today" label="Scans Today" value={data.scans_today ?? 0} />
          <Metric testid="dh-mapping" label="Mapping" value={`${data.mapped_employees} Mapped`} sub={`${data.mapped_payroll} Payroll`} />
        </div>
      )}
      {data && (data.unmapped_count > 0 || (data.mapping_conflicts || []).length > 0) && (
        <p data-testid="dh-alert" className="mt-2 flex items-center gap-1.5 text-[11.5px] font-medium text-[#B5590F]">
          <AlertTriangle className="h-3.5 w-3.5" />
          {data.unmapped_count > 0 ? `${data.unmapped_count} unmapped PIN${data.unmapped_count > 1 ? "s" : ""} today` : `${data.mapping_conflicts.length} mapping conflict(s)`}
        </p>
      )}
    </div>
  );
}

export function DeviceHealthDetailed() {
  const data = useHealth();
  const st = STATE_STYLE[data?.webhook_state] || STATE_STYLE["No Recent Activity"];
  if (!data) return <div className="liquid-glass-card grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>;
  const alerts = [];
  if (data.unmapped_count > 0) alerts.push(`${data.unmapped_count} unmapped PIN${data.unmapped_count > 1 ? "s" : ""} seen today: ${data.unmapped_pins.join(", ")}`);
  (data.mapping_conflicts || []).forEach((c) => alerts.push(`PIN ${c.pin} mapped to multiple employees: ${c.employees.join(", ")}`));
  if (data.webhook_state === "No Recent Activity") alerts.push("No recent scan/webhook activity during work hours. Check the device connection.");
  return (
    <div data-testid="device-health-detailed" className="liquid-glass-card p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[14px] font-semibold text-[#1D1D1F]">
          <Activity className="h-4.5 w-4.5 text-[#0071E3]" /> Device Health
        </div>
        <span data-testid="dh-state-detailed" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium ${st.pill}`}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: st.dot }} /> {data.webhook_state}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <Metric label="Last Scan Received" value={data.last_scan_rel} sub={data.last_scan_wita ? `${data.last_scan_wita} WITA` : null} />
        <Metric label="Last Webhook" value={data.last_webhook_rel} sub={data.last_webhook_wita ? `${data.last_webhook_wita} WITA` : null} />
        <Metric label="Scans Today" value={data.scans_today ?? 0} />
        <Metric label="Last Processing" value={data.last_processing_rel} />
        <Metric testid="dh-mapping-detailed" label="Employee Mapping" value={`${data.mapped_employees} Mapped`} sub={`${data.mapped_payroll} Payroll · ${data.mapped_attendance_only} Attn-Only`} />
        <Metric label="Unmapped PINs" value={data.unmapped_count ?? 0} />
        <Metric label="Mapping Conflicts" value={(data.mapping_conflicts || []).length} />
        <Metric label="Device" value={data.device_name || "—"} />
      </div>
      {alerts.length > 0 && (
        <div data-testid="dh-alerts" className="mt-4 space-y-1.5">
          {alerts.map((a, i) => (
            <p key={i} className="flex items-start gap-1.5 rounded-xl bg-[#FFF8F2] px-3 py-2 text-[12px] font-medium text-[#B5590F]">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {a}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
